import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  Grid,
  Typography,
  Box,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Switch,
} from "@mui/material";
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";
import { Timestamp } from "firebase/firestore"; // Import Timestamp for date conversion
import {
  calculateScholarshipStatus,
  calculateScholarshipEligible,
  calculateFinalGPA,
  calculateSeniorPacket,
  getSeniorPacketStatusText,
} from "../../../../utils/scholarshipUtils";
import {
  ProgramDeliveryDropdown,
  TpHeiDropdown,
} from "../../CourseCatalog/DropDowns";
import InstructorDropdown from "../../DeliveryDetails/inc/InstructorDropdown";

// Tartan ID (SCCID) is always 7 digits — pad with leading zeros if shorter.
const padTartanId = (value) => {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return trimmed;
  return trimmed.padStart(7, "0");
};

// Single source of truth for the form's blank state — used for both the
// initial useState and the post-submit reset so they can never drift apart.
const getInitialFormData = () => ({
  App_StudentID: "",
  Application_Dt: "",
  LastName: "",
  FirstName: "",
  ProgramDeliveryID: "",
  DeliveryDetailID: "",
  scholarship: false,
  MI: "",
  SCC_Email: "",
  Alternate_Email: "",
  SCCID: "",
  HSGY: "",
  Password: "",
  Sem7GPA: "",
  Sem8GPA: "",
  CTE_requirement: false,
  CurrentEnrollmentStatus: "",
  Address: "",
  City: "",
  State: "",
  Zip: "",
  DOB: "",
  Secondary_Delivery_ID: "",
  Updated_By: "",
  Last_Updated: "",
  SSN: "",
  SSN_encrypted: "",
  SSN_Dashes: "",
  Last_Four_SSN: "",
  PhoneNumber: "",
  Race: "",
  Gender: "",
  HomeSchool: "",
  HomeSchoolID: "",
  SSID: "",
  MM3_ALG_II: false,
  MM3_PreCalc: false,
  Note: "",
  Residency_Status: "",
  OutReach_Status: "",
  academic_program_interest: "",
  final_gpa: "",
  senior_packet: false,
  scholarship_eligible: false,
  added_to_portal: false,
  verified_SSID: false,
  CCP_participant: false,
  early_applicant_first_term: "",
  SC_Institution_Id_Nb: "",
  reach_contact_id: "",
  sc_ctag_permission: false,
});

const Add = ({ onRecordAdded, addDocument }) => {
  // Use the passed addDocument function instead of the hook
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  // Bumped on every successful reset so the Partner Info dropdowns
  // (ProgramDeliveryDropdown, InstructorDropdown, Site of Delivery) are
  // forced to fully remount rather than trust their own internal state to
  // catch up with a reset formData value.
  const [formResetKey, setFormResetKey] = useState(0);

  // Fetch delivery details for dropdown
  const {
    data: deliveryDetails,
    loading: deliveryDetailsLoading,
    error: deliveryDetailsError,
  } = useFirestore("DeliveryDetails");

  // Fetch program delivery data to get site relationships
  const {
    data: programDelivery,
    loading: programDeliveryLoading,
    error: programDeliveryError,
  } = useFirestore("ProgramDelivery");

  // Fetch sites data to get site names
  const {
    data: sites,
    loading: sitesLoading,
    error: sitesError,
  } = useFirestore("Sites");

  // Fetch pathway programs data for dropdown
  const {
    data: pathwayPrograms,
    loading: pathwayProgramsLoading,
    error: pathwayProgramsError,
  } = useFirestore("PathwaysPrograms");

  // Fetch SSN status codes for dropdown
  const {
    data: ssnStatusCodes,
    loading: ssnStatusCodesLoading,
    error: ssnStatusCodesError,
  } = useFirestore("SsnStatusCode");
  const [formData, setFormData] = useState(getInitialFormData);

  // Function to get site name for a delivery detail
  const getSiteName = (deliveryDetail) => {
    if (!deliveryDetail || !programDelivery || !sites) return "";

    // Find the program delivery record for this delivery detail
    const programDeliveryRecord = programDelivery.find(
      (pd) => pd.ProgramDeliveryID === deliveryDetail.ProgramDeliveryID,
    );

    if (!programDeliveryRecord) return "";

    // Find the site record using the SiteOfDelivery field
    const siteRecord = sites.find(
      (site) =>
        site.SiteName === programDeliveryRecord.SiteOfDelivery ||
        site.SiteID === programDeliveryRecord.SiteOfDelivery ||
        site.SiteNameShort === programDeliveryRecord.SiteOfDelivery,
    );

    return siteRecord
      ? siteRecord.SiteName
      : programDeliveryRecord.SiteOfDelivery || "";
  };

  // Derive TP Pathway Name directly from Partner: ProgramDelivery.PathwayID
  // → PathwaysPrograms.PathwayName (no DeliveryDetailID needed — Pathway
  // Abbreviation and TP Pathway are both a pure function of Partner alone).
  const tpPathwayName = React.useMemo(() => {
    if (!formData.ProgramDeliveryID || !programDelivery || !pathwayPrograms)
      return "";
    const pdid = String(formData.ProgramDeliveryID).trim();
    const pd = programDelivery.find(
      (p) => String(p.ProgramDeliveryID).trim() === pdid,
    );
    if (!pd) return "";
    const pathwayId = String(pd.PathwayID || "").trim();
    const pathway = pathwayPrograms.find(
      (p) => String(p.PathwayID || p.id || "").trim() === pathwayId,
    );
    return pathway?.PathwayName || "";
  }, [formData.ProgramDeliveryID, programDelivery, pathwayPrograms]);

  // Resolve the fields that are a pure function of Partner alone (Pathway
  // Abbreviation, via ProgramDelivery → PathwaysPrograms) — no
  // DeliveryDetailID needed, unlike the rest of the Partner Info section.
  const deriveFieldsFromPartner = (programDeliveryId) => {
    const pd = (programDelivery || []).find(
      (p) =>
        String(p.ProgramDeliveryID).trim() ===
        String(programDeliveryId || "").trim(),
    );
    const pathwayId = String(pd?.PathwayID || "").trim();
    const pathway = (pathwayPrograms || []).find(
      (p) => String(p.PathwayID || p.id || "").trim() === pathwayId,
    );
    return {
      Secondary_Delivery_ID: pathway?.PathwayAbbrev || "",
    };
  };

  // Resolve HEI Program (and, defensively, Partner/Pathway Abbreviation) from
  // a specific DeliveryDetailID — used when the user directly overrides Site
  // of Delivery rather than letting it cascade from Instructor + Partner.
  const deriveFieldsFromDeliveryDetailId = (ddId) => {
    // DeliveryDetails records have never actually had a DeliveryDetailsID
    // field populated (verified against live data — it's undefined on
    // every document) — the real unique identifier is the Firestore
    // document id, same as every other collection accessed via
    // useFirestore's {id: doc.id, ...doc.data()} shape.
    const dd = (deliveryDetails || []).find(
      (d) => String(d.id) === String(ddId),
    );
    if (!dd) {
      return { reach_contact_id: "" };
    }
    return {
      ProgramDeliveryID: dd.ProgramDeliveryID || "",
      reach_contact_id: dd.HEIProgramCode || "",
      ...deriveFieldsFromPartner(dd.ProgramDeliveryID),
    };
  };

  // Active DeliveryDetails records this instructor actually delivers.
  const getInstructorDeliveryDetails = (instructorId) =>
    (deliveryDetails || []).filter(
      (dd) =>
        String(dd.InstructorID) === String(instructorId) &&
        dd.Active !== false,
    );

  // Resolve Site of Delivery (and HEI Program) from the Instructor+Partner
  // pair: auto-select when exactly one DeliveryDetails record matches both,
  // otherwise clear so the (now narrowed) dropdown forces an explicit pick.
  const resolveSiteOfDeliveryForPartner = (instructorId, programDeliveryId) => {
    const matches = getInstructorDeliveryDetails(instructorId).filter(
      (dd) => String(dd.ProgramDeliveryID) === String(programDeliveryId),
    );
    if (matches.length === 1) {
      return {
        DeliveryDetailID: matches[0].id,
        reach_contact_id: matches[0].HEIProgramCode || "",
      };
    }
    return { DeliveryDetailID: "", reach_contact_id: "" };
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prevData) => {
      const updated = {
        ...prevData,
        [name]: type === "checkbox" ? checked : value,
      };

      if (name === "academic_program_interest") {
        // Instructor changed — resolve Partner from the distinct partners
        // this instructor delivers. Exactly one match auto-selects it; zero
        // or multiple clears Partner so the (now narrowed) dropdown forces
        // an explicit pick instead of guessing wrong.
        const distinctPartnerIds = [
          ...new Set(
            getInstructorDeliveryDetails(value).map((dd) =>
              String(dd.ProgramDeliveryID),
            ),
          ),
        ].filter(Boolean);
        const newPartnerId =
          distinctPartnerIds.length === 1 ? distinctPartnerIds[0] : "";
        updated.ProgramDeliveryID = newPartnerId;

        if (newPartnerId) {
          Object.assign(updated, deriveFieldsFromPartner(newPartnerId));
          Object.assign(
            updated,
            resolveSiteOfDeliveryForPartner(value, newPartnerId),
          );
        } else {
          updated.Secondary_Delivery_ID = "";
          updated.DeliveryDetailID = "";
          updated.reach_contact_id = "";
        }
      } else if (name === "ProgramDeliveryID") {
        // Partner changed (manually, when the instructor delivers more than
        // one) — Pathway Abbreviation/TP Pathway derive directly from
        // Partner, then Site of Delivery + HEI Program resolve from the
        // Instructor+Partner pair.
        Object.assign(updated, deriveFieldsFromPartner(value));
        Object.assign(
          updated,
          resolveSiteOfDeliveryForPartner(
            prevData.academic_program_interest,
            value,
          ),
        );
      } else if (name === "DeliveryDetailID") {
        Object.assign(updated, deriveFieldsFromDeliveryDetailId(value));
      }

      return updated;
    });
  };

  // Render field function matching Edit.js
  const renderField = (field, gridSize = 6) => {
    const fieldLabels = {
      Student_Guid: "Student GUID",
      App_StudentID: "Status",
      DOB: "DOB",
      Application_Dt: "App Date",
      SSN_Status: "App Status",
      SSN_Status_Code: "SSN Status Code",
      verified_SSID: "Verified SSID",
      sc_ctag_permission: "SC CTAG Permission",
      HSGY: "HSGY",
      HomeSchool: "HomeSchool",
      HomeSchoolID: "HomeSchool ID",
      CCP_participant: "CCP Participant",
      SCC_Email: "Sinclair Email",
      Alternate_Email: "Alternate Email",
      OutReach_Status: "Outreach Status",
      PhoneNumber: "Phone",
      CTE_requirement: "CTE",
      scholarship: "Scholarship",
      final_gpa: "Final GPA",
      senior_packet: "Senior Packet",
      scholarship_eligible: "Scholarship Eligible",
      MM3_ALG_II: "ALG II ≥ B",
      MM3_PreCalc: "PreCalc ≥ B",
      ProgramDeliveryID: "Tech Prep Partner",
      DeliveryDetailID: "Site Of Delivery",
      SCCID: "Tartan ID",
      academic_program_interest: "Instructor",
      Sem7GPA: "Semester 7 GPA",
      Sem8GPA: "Semester 8 GPA",
      Secondary_Delivery_ID: "Pathway Abbreviation",
      reach_contact_id: "HEI Program",
      CurrentEnrollmentStatus: "Current Enrollment Status",
    };

    const label = fieldLabels[field] || field.replace(/_/g, " ");

    return (
      <Grid item xs={12} sm={gridSize} key={field}>
        {field === "senior_packet" ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "grey.50",
              }}
            >
              <Typography
                variant="body1"
                color="text.primary"
                fontWeight="medium"
              >
                {getSeniorPacketStatusText(formData) || "Not Applicable"}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Calculated from HSGY, CTE requirement, Tartan ID, and GPAs
            </Typography>
          </Box>
        ) : field === "scholarship_eligible" ||
          field.includes("scholarship_eligible") ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Scholarship Status
            </Typography>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "grey.50",
              }}
            >
              <Typography
                variant="body1"
                color="text.primary"
                fontWeight="medium"
              >
                {calculateScholarshipStatus(formData) || "Not Applicable"}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Calculated from scholarship status formula
            </Typography>
          </Box>
        ) : field === "scholarship" ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Scholarship Eligibility Status
            </Typography>
            <Box
              sx={{
                p: 2,
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                backgroundColor: "grey.50",
              }}
            >
              <Typography
                variant="body1"
                color="text.primary"
                fontWeight="medium"
              >
                {calculateScholarshipEligible(formData)
                  ? "Eligible for Scholarship"
                  : "Not Eligible for Scholarship"}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              Calculated based on scholarship status
            </Typography>
          </Box>
        ) : typeof formData[field] === "boolean" &&
          field !== "senior_packet" &&
          !field.includes("scholarship_eligible") &&
          field !== "scholarship" ? (
          <Box sx={{ mt: 2, mb: 1 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {label}
            </Typography>
            <Box display="flex" alignItems="center" gap={2}>
              <Switch
                checked={formData[field] || false}
                onChange={handleChange}
                name={field}
                color="primary"
                size="medium"
              />
              <Typography variant="body2" color="text.secondary">
                {formData[field] ? "Yes" : "No"}
              </Typography>
            </Box>
          </Box>
        ) : field === "ProgramDeliveryID" ? (
          (() => {
            const instructorId = formData.academic_program_interest;
            const distinctPartnerIds = instructorId
              ? [
                  ...new Set(
                    getInstructorDeliveryDetails(instructorId).map((dd) =>
                      String(dd.ProgramDeliveryID),
                    ),
                  ),
                ].filter(Boolean)
              : null;
            return (
              <ProgramDeliveryDropdown
                value={formData[field] || ""}
                onChange={handleChange}
                name={field}
                label={label}
                size="small"
                margin="normal"
                allowedIds={
                  distinctPartnerIds && distinctPartnerIds.length > 0
                    ? distinctPartnerIds
                    : undefined
                }
              />
            );
          })()
        ) : field === "academic_program_interest" ? (
          <InstructorDropdown
            value={formData[field] || ""}
            onChange={handleChange}
            name={field}
            label={label}
            size="small"
            margin="normal"
          />
        ) : field === "Sem8GPA" ? (
          <TextField
            label={label}
            name={field}
            value={formData[field] || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            type="number"
            inputProps={{ step: "0.01", min: "0", max: "4" }}
          />
        ) : field === "reach_contact_id" ? (
          <TpHeiDropdown
            value={formData[field] || ""}
            onChange={handleChange}
            name={field}
            label={label}
            size="small"
            margin="normal"
          />
        ) : field === "SSN_Status_Code" ? (
          <FormControl fullWidth margin="normal" size="small">
            <InputLabel>{label}</InputLabel>
            <Select
              name={field}
              value={formData[field] || ""}
              onChange={handleChange}
              label={label}
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {ssnStatusCodesLoading ? (
                <MenuItem disabled>
                  <em>Loading...</em>
                </MenuItem>
              ) : ssnStatusCodesError ? (
                <MenuItem disabled>
                  <em>Error loading SSN status codes</em>
                </MenuItem>
              ) : ssnStatusCodes && ssnStatusCodes.length > 0 ? (
                ssnStatusCodes.map((statusCode) => (
                  <MenuItem
                    key={statusCode.id}
                    value={statusCode.SSN_Status_Code}
                  >
                    {statusCode.SSN_Status} ({statusCode.SSN_Status_Code})
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <em>No SSN status codes available</em>
                </MenuItem>
              )}
            </Select>
          </FormControl>
        ) : field === "DeliveryDetailID" ? (
          (() => {
            const instructorId = formData.academic_program_interest;
            const partnerId = formData.ProgramDeliveryID;
            let options;
            if (instructorId && partnerId) {
              options = getInstructorDeliveryDetails(instructorId).filter(
                (dd) => String(dd.ProgramDeliveryID) === String(partnerId),
              );
            } else if (partnerId) {
              options = (deliveryDetails || []).filter(
                (dd) =>
                  String(dd.ProgramDeliveryID) === String(partnerId) &&
                  dd.Active !== false,
              );
            } else if (instructorId) {
              options = getInstructorDeliveryDetails(instructorId);
            } else {
              options = (deliveryDetails || []).filter(
                (d) => d.Active !== false,
              );
            }
            const isNarrowed = Boolean(instructorId || partnerId);

            return (
              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>{label}</InputLabel>
                <Select
                  name={field}
                  value={formData[field] || ""}
                  onChange={handleChange}
                  label={label}
                  disabled={
                    deliveryDetailsLoading ||
                    programDeliveryLoading ||
                    sitesLoading
                  }
                >
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {deliveryDetailsLoading ||
                  programDeliveryLoading ||
                  sitesLoading ? (
                    <MenuItem disabled>
                      <em>Loading delivery details...</em>
                    </MenuItem>
                  ) : deliveryDetailsError ||
                    programDeliveryError ||
                    sitesError ? (
                    <MenuItem disabled>
                      <em>Error loading delivery details</em>
                    </MenuItem>
                  ) : options.length > 0 ? (
                    options
                      .map((detail) => ({
                        detail,
                        siteName: getSiteName(detail),
                      }))
                      .sort((a, b) => {
                        const nameA = (
                          a.siteName ||
                          a.detail.HEIProgramCode ||
                          ""
                        ).toLowerCase();
                        const nameB = (
                          b.siteName ||
                          b.detail.HEIProgramCode ||
                          ""
                        ).toLowerCase();
                        return nameA.localeCompare(nameB);
                      })
                      .map(({ detail, siteName }) => {
                        const displayName =
                          siteName ||
                          detail.HEIProgramCode ||
                          "No Site/Program";
                        return (
                          <MenuItem key={detail.id} value={detail.id}>
                            {displayName} ({detail.HEIProgramCode || detail.id})
                          </MenuItem>
                        );
                      })
                  ) : (
                    <MenuItem disabled>
                      <em>No delivery details available</em>
                    </MenuItem>
                  )}
                </Select>
                {isNarrowed && (
                  <Typography variant="caption" color="text.secondary">
                    {instructorId && partnerId
                      ? "Showing only sites for the selected instructor and partner"
                      : partnerId
                        ? "Showing only sites for the selected partner"
                        : "Showing only sites for the selected instructor"}
                  </Typography>
                )}
              </FormControl>
            );
          })()
        ) : field === "SCCID" ? (
          <TextField
            label={label}
            name={field}
            value={formData[field] || ""}
            onChange={handleChange}
            onBlur={(e) => {
              const padded = padTartanId(e.target.value);
              if (padded !== formData.SCCID) {
                setFormData((prev) => ({ ...prev, SCCID: padded }));
              }
            }}
            fullWidth
            margin="normal"
            size="small"
            helperText="Automatically padded to 7 digits with leading zeros"
          />
        ) : field === "final_gpa" ? (
          <TextField
            label={label}
            name={field}
            value={calculateFinalGPA(formData).toFixed(2)}
            fullWidth
            margin="normal"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: "#f5f5f5",
                color: "#666",
              },
              "& .MuiInputBase-input": {
                color: "#666",
              },
            }}
            helperText="Calculated from Sem7GPA and Sem8GPA"
          />
        ) : field === "Secondary_Delivery_ID" ? (
          <TextField
            label={label}
            name={field}
            value={formData[field] || ""}
            fullWidth
            margin="normal"
            size="small"
            InputProps={{
              readOnly: true,
            }}
            sx={{
              "& .MuiInputBase-root": {
                backgroundColor: "#f5f5f5",
                color: "#666",
              },
              "& .MuiInputBase-input": {
                color: "#666",
              },
            }}
            helperText="Auto-filled from selected partner"
          />
        ) : (
          <TextField
            label={label}
            name={field}
            value={formData[field] || ""}
            onChange={handleChange}
            fullWidth
            margin="normal"
            size="small"
            type={
              ["Application_Dt", "DOB"].includes(field)
                ? "date"
                : field.includes("GPA")
                  ? "number"
                  : "text"
            }
            InputLabelProps={
              ["Application_Dt", "DOB"].includes(field) ? { shrink: true } : {}
            }
            inputProps={
              field.includes("GPA") ? { step: "0.01", min: "0", max: "4" } : {}
            }
          />
        )}
      </Grid>
    );
  };

  const handleSubmit = async () => {
    // HSGY (graduation year) drives the Active/Archived Students filters —
    // a student saved without it is invisible on both pages even though
    // the document is real, so block the save and tell the user why
    // instead of silently creating an unreachable record.
    if (!formData.HSGY || !String(formData.HSGY).trim()) {
      onRecordAdded(
        false,
        "HSGY (graduation year) is required. Without it, this student will not appear in the Active or Archived Students lists.",
      );
      return;
    }

    setLoading(true);
    try {
      const dataToSave = { ...formData };

      // Convert string dates to Firestore Timestamps before saving
      const dateFields = ["Application_Dt", "DOB"];

      dateFields.forEach((field) => {
        if (dataToSave[field] && typeof dataToSave[field] === "string") {
          try {
            const date = new Date(dataToSave[field]);
            if (!isNaN(date.getTime())) {
              dataToSave[field] = Timestamp.fromDate(date);
            } else {
              dataToSave[field] = null;
            }
          } catch (error) {
            console.error(`Error converting ${field} to timestamp:`, error);
            dataToSave[field] = null;
          }
        } else if (!dataToSave[field]) {
          dataToSave[field] = null;
        }
      });

      // Set server timestamp for audit fields
      dataToSave.created_at = Timestamp.now();
      dataToSave.Last_Updated = Timestamp.now();

      // Store HSGY as a trimmed 4-digit String, matching the existing
      // TpStudents data, so the Active/Archived Students range queries
      // (which compare against a String) keep matching this record.
      if (dataToSave.HSGY !== "" && dataToSave.HSGY != null) {
        dataToSave.HSGY = String(dataToSave.HSGY).trim();
      } else {
        dataToSave.HSGY = null;
      }

      // Tartan ID (SCCID) is always 7 digits — pad with leading zeros.
      dataToSave.SCCID = padTartanId(dataToSave.SCCID) || null;

      // Calculate and save the final GPA
      dataToSave.final_gpa = calculateFinalGPA(formData);

      // Calculate and save the senior packet status
      dataToSave.senior_packet = calculateSeniorPacket(formData);

      // Calculate and save the scholarship eligibility
      dataToSave.scholarship_eligible = calculateScholarshipEligible(formData);

      await addDocument(dataToSave);
      setLoading(false);
      onRecordAdded(true, "Student record added successfully!");
      setFormData(getInitialFormData());
      // Force the Partner Info dropdowns to fully remount so none of them
      // can hold onto a stale selection from the record just submitted.
      setFormResetKey((k) => k + 1);
      setOpen(false);
    } catch (error) {
      console.error("Error adding record:", error);
      onRecordAdded(false, `Error adding record: ${error.message}`);
      setLoading(false);
    }
  };

  return (
    <div>
      <Button
        variant="contained"
        color="success"
        onClick={() => setOpen(true)}
        sx={{ marginBottom: "10px", color: "white" }}
      >
        Add
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <ModalDialogTitle onClose={() => setOpen(false)}>
          Add Student Record
        </ModalDialogTitle>
        <DialogContent>
          {/* Display Calculated Scholarship Status */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#a41e36",
              color: "white",
              borderRadius: 0,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}
            >
              Student Info
            </Typography>
          </Box>

          {/* Student Information Section */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            {renderField("FirstName", 4)}
            {renderField("MI", 2)}
            {renderField("LastName", 6)}
            {renderField("Gender", 6)}
            {renderField("Student_Guid", 6)}
            {renderField("SCCID", 6)}
            {renderField("App_StudentID", 6)}
            {renderField("DOB", 6)}
            {renderField("Application_Dt", 6)}
            {renderField("SSN_Status", 6)}
            {renderField("SSN_Status_Code", 6)}
            {renderField("verified_SSID", 3)}
            {renderField("sc_ctag_permission", 3)}
            {renderField("HSGY", 6)}
            {renderField("HomeSchool", 6)}
            {renderField("HomeSchoolID", 6)}
            {renderField("CCP_participant", 6)}
            {renderField("SCC_Email", 12)}
            {renderField("Alternate_Email", 12)}
            {renderField("OutReach_Status", 6)}
            {renderField("Address", 12)}
            {renderField("City", 6)}
            {renderField("State", 3)}
            {renderField("Zip", 3)}
            {renderField("PhoneNumber", 6)}
          </Grid>

          {/* Tech Prep Info Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#a41e36",
              color: "white",
              borderRadius: 0,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}
            >
              Tech Prep Info
            </Typography>
          </Box>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            {/* Input fields first */}
            {renderField("final_gpa", 6)}
            {renderField("Sem7GPA", 6)}
            {renderField("Sem8GPA", 6)}

            {/* Calculated fields */}
            {renderField("scholarship", 6)}
            {renderField("senior_packet", 6)}
            {renderField("scholarship_eligible", 6)}

            {/* Switch fields at the bottom */}
            {renderField("CTE_requirement", 6)}
            {renderField("MM3_ALG_II", 6)}
            {renderField("MM3_PreCalc", 6)}
            {renderField("CurrentEnrollmentStatus", 6)}
          </Grid>

          {/* Partner Info Section */}
          <Box
            sx={{
              mb: 3,
              p: 2,
              backgroundColor: "#a41e36",
              color: "white",
              borderRadius: 0,
            }}
          >
            <Typography
              variant="h6"
              gutterBottom
              sx={{ color: "white", textAlign: "center", fontWeight: "bold" }}
            >
              Partner Info
            </Typography>
          </Box>

          <Grid container spacing={2} key={formResetKey}>
            {renderField("academic_program_interest", 6)}
            {renderField("ProgramDeliveryID", 6)}
            {renderField("DeliveryDetailID", 6)}
            {renderField("Secondary_Delivery_ID", 6)}
            {renderField("reach_contact_id", 6)}

            {/* TP Pathway — read-only, derived from DDID lookup chain */}
            <Grid item xs={12} sm={6}>
              <Box sx={{ mt: 1, mb: 1 }}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  TP Pathway
                </Typography>
                <Box
                  sx={{
                    p: 1.5,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    backgroundColor: "grey.50",
                    minHeight: 40,
                  }}
                >
                  <Typography variant="body1" color="text.primary">
                    {tpPathwayName ||
                      (deliveryDetailsLoading ||
                      programDeliveryLoading ||
                      pathwayProgramsLoading
                        ? "Loading…"
                        : "—")}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Derived from Partner → Pathway
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} />
      </Dialog>
    </div>
  );
};

export default Add;
